import cn from "classnames";
import { toastServerError } from "helpers";
import { useDebouncedValue } from "hooks";
import { useGetAllCategories } from "modules/categories";
import { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Pagination, SearchInput, Select } from "ui";

import {
  useAvailableEnrichmentProfiles,
  useEnrichBatch,
  useEnrichmentList,
  useEnrichProduct
} from "../../apis";
import {
  EnrichmentTable,
  ProductAttributesModal,
  SectionCard
} from "../../components";
import {
  ENRICHMENT_PAGE_SIZE,
  PRODUCT_GENDER_LABELS,
  PRODUCT_SEASON_LABELS,
  PRODUCT_STYLE_LABELS
} from "../../constants";
import { buildCategoryOptions, flattenCategoryTree } from "../../helpers";
import {
  EEnrichmentListStatusFilter,
  EnrichmentListItem,
  EProductGender,
  EProductSeason,
  EProductStyle
} from "../../recommendation.types";

import styles from "./EnrichmentCoverage.module.scss";

const FILTER_OPTIONS: Array<{
  label: string;
  value: EEnrichmentListStatusFilter | "all";
}> = [
  { label: "All", value: "all" },
  { label: "Enriched", value: EEnrichmentListStatusFilter.Enriched },
  { label: "Un-enriched", value: EEnrichmentListStatusFilter.Unenriched },
  { label: "Failed", value: EEnrichmentListStatusFilter.Failed },
  // Deliberately excluded by their category's rule. Kept reviewable rather than
  // hidden: "un-enriched" no longer counts them, so this is the only way to see
  // what was excluded and check the exclusion was meant.
  { label: "Skipped", value: EEnrichmentListStatusFilter.Skipped }
];

const BATCH_LIMIT = 20;

const withAllOption = (options: Array<{ label: string; value: string }>) => [
  { label: "All", value: "" },
  ...options
];

const labelOptions = <T extends string>(labels: Record<T, string>) =>
  withAllOption(
    (Object.entries(labels) as Array<[T, string]>).map(([value, label]) => ({
      label,
      value
    }))
  );

const GENDER_OPTIONS = labelOptions(PRODUCT_GENDER_LABELS);
const SEASON_OPTIONS = labelOptions(PRODUCT_SEASON_LABELS);
const STYLE_OPTIONS = labelOptions(PRODUCT_STYLE_LABELS);

const EnrichmentCoverage = () => {
  const { t } = useTranslation();

  const [filter, setFilter] = useState<EEnrichmentListStatusFilter | "all">(
    "all"
  );
  const [page, setPage] = useState(1);
  const [enrichingId, setEnrichingId] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<EnrichmentListItem | null>(
    null
  );
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [profileKey, setProfileKey] = useState("");
  const [gender, setGender] = useState("");
  const [season, setSeason] = useState("");
  const [style, setStyle] = useState("");
  const [editedOnly, setEditedOnly] = useState(false);

  const debouncedSearch = useDebouncedValue(search, 500);

  const { categories } = useGetAllCategories();
  const { availableProfiles } = useAvailableEnrichmentProfiles();

  // Any level: a parent narrows the list to its whole branch.
  const categoryOptions = useMemo(() => {
    const flat = flattenCategoryTree(categories);

    return withAllOption(
      buildCategoryOptions(flat, flat).map((option) => ({
        label: String(option.label),
        value: option.value
      }))
    );
  }, [categories]);

  const profileOptions = useMemo(
    () =>
      withAllOption(
        availableProfiles.map((profile) => ({
          label: profile.label,
          value: profile.key
        }))
      ),
    [availableProfiles]
  );

  const listParams = useMemo(
    () => ({
      status: filter === "all" ? undefined : filter,
      search: debouncedSearch.trim() || undefined,
      categoryId: categoryId || undefined,
      profileKey: profileKey || undefined,
      gender: (gender || undefined) as EProductGender | undefined,
      season: (season || undefined) as EProductSeason | undefined,
      style: (style || undefined) as EProductStyle | undefined,
      edited: editedOnly ? "true" : undefined,
      page,
      take: ENRICHMENT_PAGE_SIZE
    }),
    [
      filter,
      debouncedSearch,
      categoryId,
      profileKey,
      gender,
      season,
      style,
      editedOnly,
      page
    ]
  );

  // Every filter change starts again from the first page.
  const withPageReset =
    <T,>(setter: (value: T) => void) =>
    (value: T) => {
      setter(value);
      setPage(1);
    };

  const { items, total, isLoading } = useEnrichmentList(listParams);

  // Lightweight coverage counters (one row each, just for the totals).
  const { total: allTotal } = useEnrichmentList({ page: 1, take: 1 });
  const { total: enrichedTotal } = useEnrichmentList({
    status: EEnrichmentListStatusFilter.Enriched,
    page: 1,
    take: 1
  });

  const coveragePct = allTotal
    ? Math.round((enrichedTotal / allTotal) * 100)
    : 0;

  const { mutate: enrichOne } = useEnrichProduct({
    onSuccess: () => {
      toast.success(t("Product enriched"));
      setEnrichingId(null);
    },
    onError: (error) => {
      setEnrichingId(null);
      toastServerError(error);
    }
  });

  const { mutate: runBatch, isLoading: isBatchRunning } = useEnrichBatch({
    onSuccess: (result) =>
      toast.success(
        `${t("Batch done")}: ${result.enriched} ${t("enriched")}, ${
          result.failed
        } ${t("failed")}, ${result.skippedNoImage} ${t("skipped (no image)")}`
      ),
    onError: toastServerError
  });

  const handleEnrich = (item: EnrichmentListItem) => {
    setEnrichingId(item.productId);
    enrichOne(item.productId);
  };

  const handleFilterChange = (value: EEnrichmentListStatusFilter | "all") => {
    setFilter(value);
    setPage(1);
  };

  return (
    <div className={styles.coverage}>
      <SectionCard
        accentColor="#16C350"
        title={t("Enrichment coverage")}
        subtitle={t(
          "AI tags each product with gender / type / material / style / season / colors, from its photo or its description depending on the category's rule. A background sweep runs every ~10 min — these controls are for visibility and one-off fixes."
        )}
        action={
          <Button
            loading={isBatchRunning}
            onClick={() => runBatch({ limit: BATCH_LIMIT })}
          >
            {t("Run batch")} ({BATCH_LIMIT})
          </Button>
        }
      >
        <div className={styles.coverage__meter}>
          <div className={styles.coverage__meterHead}>
            <span className={styles.coverage__count}>
              {enrichedTotal} / {allTotal}
            </span>
            <span className={styles.coverage__pct}>
              {coveragePct}% {t("enriched")}
            </span>
          </div>
          <div className={styles.coverage__track}>
            <div
              className={styles.coverage__fill}
              style={{ width: `${coveragePct}%` }}
            />
          </div>
        </div>
      </SectionCard>

      <div className={styles.coverage__filters}>
        {FILTER_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            className={cn(styles.coverage__filter, {
              [styles.coverage__filter_active]: option.value === filter
            })}
            onClick={() => handleFilterChange(option.value)}
          >
            {t(option.label)}
          </button>
        ))}

        <span className={styles.coverage__total}>
          {total} {t("products")}
        </span>
      </div>

      <div className={styles.coverage__search}>
        <div className={styles.coverage__searchInput}>
          <SearchInput
            value={search}
            placeholder="Search by name, short ID or SKU"
            onChange={withPageReset(setSearch)}
          />
        </div>

        <Checkbox
          label={t("Edited by hand")}
          checked={editedOnly}
          onChange={(event) => withPageReset(setEditedOnly)(event.target.checked)}
        />
      </div>

      <div className={styles.coverage__attributeFilters}>
        <Select
          label={t("Category")}
          options={categoryOptions}
          value={categoryId}
          withFilterInput
          onChange={(value) => withPageReset(setCategoryId)(value as string)}
        />
        <Select
          label={t("Attribute set")}
          options={profileOptions}
          value={profileKey}
          onChange={(value) => withPageReset(setProfileKey)(value as string)}
        />
        <Select
          label={t("Gender")}
          options={GENDER_OPTIONS}
          value={gender}
          onChange={(value) => withPageReset(setGender)(value as string)}
        />
        <Select
          label={t("Season")}
          options={SEASON_OPTIONS}
          value={season}
          onChange={(value) => withPageReset(setSeason)(value as string)}
        />
        <Select
          label={t("Style")}
          options={STYLE_OPTIONS}
          value={style}
          onChange={(value) => withPageReset(setStyle)(value as string)}
        />
      </div>

      <EnrichmentTable
        items={items}
        isLoading={isLoading}
        enrichingId={enrichingId}
        onEnrich={handleEnrich}
        onRowClick={setSelectedItem}
      />

      <Pagination
        currentPage={page}
        totalItems={total}
        perPage={ENRICHMENT_PAGE_SIZE}
        onChange={setPage}
      />

      <ProductAttributesModal
        item={selectedItem}
        onClose={() => setSelectedItem(null)}
      />
    </div>
  );
};

export default EnrichmentCoverage;
